// API response shapes for /v1/survivor-games and /v1/survivor-participations.
// Naming: code uses English (Survivor*), URLs use /sobrevivente, UI uses "Sobrevivente".
import type { TeamType } from '@/shared/lib/teamType'

export const SurvivorGameStatus = {
  RegistrationOpen: 'REGISTRATION_OPEN',
  Active:           'ACTIVE',
  Finished:         'FINISHED',
} as const
export type SurvivorGameStatus = (typeof SurvivorGameStatus)[keyof typeof SurvivorGameStatus]

export const SurvivorPickResult = {
  Pending: 'PENDING',
  Won:     'WON',
  Lost:    'LOST',
  Forfeit: 'FORFEIT',
} as const
export type SurvivorPickResult = (typeof SurvivorPickResult)[keyof typeof SurvivorPickResult]

// Provisional mid-round outcome of a still-PENDING pick, derived from the picked team's match.
// Distinct from the authoritative SurvivorPickResult, which only resolves at Finalize.
export const SurvivorLiveResult = {
  Won:  'WON',   // team already won its GW match (regulation/ET) — provisionally through
  Lost: 'LOST',  // team's GW match finished without an outright win — provisionally out
  Live: 'LIVE',  // team's GW match is in progress
} as const
export type SurvivorLiveResult = (typeof SurvivorLiveResult)[keyof typeof SurvivorLiveResult]

// ── List item (GET /v1/survivor-games) ────────────────────────────────────────

// Current round of the game plus the caller pick in it. Home renders its status block from
// this, so the list carries it rather than costing one request per game. Omitted (not null)
// when the competition has no gameweeks imported yet.
export type SurvivorCurrentRound = {
  gameweekId:        string
  gameweekNumber:    number
  phase:             string
  firstMatchAt:      string   // ISO 8601 UTC — the pick deadline
  isPickLocked:      boolean
  myPickTeamId?:     string
  myPickTeamCode?:   string
  myPickTeamName?:   string
  myPickResult?:     SurvivorPickResult
  myPickLiveResult?: SurvivorLiveResult
}

export type SurvivorGameListItem = {
  id:               string
  name:             string
  joinCode:         string
  status:           SurvivorGameStatus
  competitionId:    string
  competitionName:  string
  ownerId:          string
  participantCount: number
  aliveCount:       number
  isAlive:          boolean
  isChampion:       boolean
  seasonNumber:     number
  currentRound?:    SurvivorCurrentRound
}

// ── Detail (GET /v1/survivor-games/{id}) ──────────────────────────────────────

export type SurvivorGameDetail = {
  id:                string
  name:              string
  joinCode:          string
  status:            SurvivorGameStatus
  competitionId:     string
  competitionName:   string
  ownerId:           string
  ownerDisplayName:  string
  maxParticipants:   number
  participantCount:  number
  aliveCount:        number
  myParticipationId: string
  myTeamName:        string | null
  isAlive:           boolean
  isChampion:        boolean
  isOwner:           boolean
  createdAt:         string
  finishedAt?:       string
  // ── Season / series context ────────────────────────────────────────────────
  seriesId:          string
  seasonNumber:      number
  nextSeasonGameId?: string   // set once the owner has started the following season
  canRestart:        boolean
  // Last round the game actually played, across all members. The round browser stops here on
  // a finished game, so an eliminated player can still walk through the rounds they missed.
  lastPlayedGameweekNumber?: number
}

// ── Hall of fame (GET /v1/survivor-games/{id}/hall-of-fame) ───────────────────

export type SurvivorChampion = {
  userId:      string
  displayName: string
}

export type SurvivorSeason = {
  gameId:           string
  seasonNumber:     number
  status:           SurvivorGameStatus
  competitionName:  string
  finishedAt?:      string
  participantCount: number
  iParticipated:    boolean
  iWasChampion:     boolean
  champions:        SurvivorChampion[]
}

// ── Standings (GET /v1/survivor-games/{id}/standings) ─────────────────────────

export type SurvivorStandingEntry = {
  participationId:            string
  userId:                     string
  displayName:                string
  isAlive:                    boolean
  isChampion:                 boolean
  eliminatedAtGameweekId?:    string
  eliminatedAtGameweekNumber?: number
}

export type SurvivorStandings = {
  alive:      SurvivorStandingEntry[]
  eliminated: SurvivorStandingEntry[]
  champions:  SurvivorStandingEntry[]
}

// ── My picks (GET /v1/survivor-games/{id}/my-picks) ───────────────────────────

export type SurvivorPickHistoryEntry = {
  pickId:         string
  gameweekId:     string
  gameweekNumber: number
  teamId?:        string
  teamCode?:      string
  teamName?:      string
  result:         SurvivorPickResult
  submittedAt:    string
}

// ── Peer picks (GET /v1/survivor-games/{id}/picks?gameweekId=) ────────────────

export type SurvivorPeerPick = {
  participationId: string
  userId:          string
  displayName:     string
  teamId?:         string
  teamCode?:       string
  teamName?:       string
  result:          SurvivorPickResult
  liveResult?:     SurvivorLiveResult   // provisional, only set while result is PENDING
  submittedAt:     string
}

// ── Available teams (GET /v1/survivor-games/{id}/available-teams?gameweekId=) ─

// teamType gates the country flag: a club code like "CAM" (Atlético Mineiro) or "GRE"
// (Grêmio) resolves to Cameroon / Greece, and club crests are licensed art we do not ship.
export type SurvivorAvailableTeam = {
  teamId:         string
  code:           string
  name:           string
  teamType:       TeamType
  matchId:        string
  opponentTeamId?: string
  opponentCode?:  string
  opponentName?:  string
  isHome:         boolean
  kickoffAt:      string
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export type CreateSurvivorGameInput = {
  name:             string
  competitionId:    string
  maxParticipants?: number
}

export type CreateSurvivorGameResult = {
  gameId:   string
  name:     string
  joinCode: string
}

export type JoinSurvivorGameResult = {
  participationId: string
  gameId:          string
  joinedAt:        string
}

export type SubmitSurvivorPickInput = {
  gameweekId: string
  teamId:     string
}

export type SubmitSurvivorPickResult = {
  pickId:      string
  gameweekId:  string
  teamId:      string
  submittedAt: string
}

export type UpdateSurvivorGameInput = {
  name?:    string
  ownerId?: string
}

export type RegenerateSurvivorInviteCodeResult = {
  joinCode: string
}

export type RestartSurvivorGameResult = {
  gameId:           string
  name:             string
  joinCode:         string
  seasonNumber:     number
  participantCount: number
}
